import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calculator as CalculatorIcon, Info } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import Calculator from '../components/Calculator';
import Input from '../components/Input';
import SEOHead from '../components/SEOHead';

const monthlyPayment = (principal: number, annualRate: number, months: number): number => {
  const i = annualRate / 100 / 12;
  if (i === 0) return principal / months;
  return (principal * i) / (1 - Math.pow(1 + i, -months));
};

const CarRefinanceCalculator: React.FC = () => {
  const [balance, setBalance] = useState('20000');
  const [currentRate, setCurrentRate] = useState('9.50');
  const [remainingTerm, setRemainingTerm] = useState('36');
  const [newRate, setNewRate] = useState('6.50');
  const [newTerm, setNewTerm] = useState('36');
  const [switchCosts, setSwitchCosts] = useState('400');

  const [res, setRes] = useState<null | {
    currentMonthly: number; newMonthly: number; monthlySaving: number;
    currentTotal: number; newTotal: number; totalSaving: number; breakEven: number | null;
  }>(null);

  useEffect(() => {
    const bal = parseFloat(balance);
    const cRate = parseFloat(currentRate);
    const cTerm = parseInt(remainingTerm, 10);
    const nRate = parseFloat(newRate);
    const nTerm = parseInt(newTerm, 10);
    const fees = parseFloat(switchCosts || '0');
    if (isNaN(bal) || bal <= 0 || isNaN(cRate) || cRate < 0 || isNaN(cTerm) || cTerm <= 0 || isNaN(nRate) || nRate < 0 || isNaN(nTerm) || nTerm <= 0 || isNaN(fees) || fees < 0) {
      setRes(null);
      return;
    }
    const currentMonthly = monthlyPayment(bal, cRate, cTerm);
    const newMonthly = monthlyPayment(bal + fees, nRate, nTerm); // fees financed into the new loan
    const currentTotal = currentMonthly * cTerm;
    const newTotal = newMonthly * nTerm;
    const monthlySaving = currentMonthly - newMonthly;
    setRes({
      currentMonthly, newMonthly, monthlySaving,
      currentTotal, newTotal, totalSaving: currentTotal - newTotal,
      breakEven: monthlySaving > 0 ? Math.ceil(fees / monthlySaving) : null,
    });
  }, [balance, currentRate, remainingTerm, newRate, newTerm, switchCosts]);

  const fmt = (v: number): string =>
    new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 2 }).format(v);

  return (
    <>
      <SEOHead
        title="Car Loan Refinance Calculator Australia | Save on Your Car Loan"
        description="See if refinancing your car loan could save you money. Compare your current loan with a new rate and term, factor in switching costs, and find your break-even point."
        keywords="car loan refinance calculator australia, refinance car loan, car loan break-even, lower car repayments, better car finance rate, refinance vehicle loan"
      />
      <Helmet>
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "FinancialProduct",
              "name": "Car Loan Refinance Calculator",
              "description": "Compare your current car loan against a new rate and term to see refinancing savings and break-even in Australia.",
              "category": "LoanOrCredit",
              "feesAndCommissionsSpecification": "Free online calculator",
              "areaServed": { "@type": "Country", "name": "Australia" },
              "interactivityType": "active"
            }
          `}
        </script>
      </Helmet>
      <Calculator
        title="Car Loan Refinance Calculator"
        description="Compare your current car loan against a new rate and term — including switching costs — to see your monthly saving, total saving and how long it takes to break even."
        icon={CalculatorIcon}
        result={res && res.monthlySaving > 0 ? `You could save ${fmt(res.monthlySaving)}/month` : res ? 'Refinancing may not save you money here' : undefined}
      >
        <div className="space-y-6">
          <div className="bg-blue-50 p-4 rounded-md mb-6">
            <div className="flex items-start">
              <Info className="h-5 w-5 text-blue-600 mr-2 mt-0.5" />
              <p className="text-sm text-blue-700">
                Enter your current payout balance and rate, then a new rate and term. Switching costs (exit fees plus the new loan's
                establishment fee) are financed into the new loan, and the break-even shows how many months of savings recoup them.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <Input label="Current Loan Balance (payout)" value={balance} onChange={setBalance} unit="$" placeholder="e.g. 20000" />
            <Input label="Switching Costs (exit + set-up fees)" value={switchCosts} onChange={setSwitchCosts} unit="$" placeholder="e.g. 400" />
            <Input label="Current Interest Rate (p.a.)" value={currentRate} onChange={setCurrentRate} unit="%" placeholder="e.g. 9.50" />
            <Input label="Remaining Term (months)" value={remainingTerm} onChange={setRemainingTerm} unit="mo" placeholder="e.g. 36" />
            <Input label="New Interest Rate (p.a.)" value={newRate} onChange={setNewRate} unit="%" placeholder="e.g. 6.50" />
            <Input label="New Loan Term (months)" value={newTerm} onChange={setNewTerm} unit="mo" placeholder="e.g. 36" />
          </div>

          {res && (
            <div className="mt-8 bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
              <div className="bg-blue-600 text-white px-6 py-4">
                <h2 className="text-xl font-semibold">Refinance Comparison</h2>
              </div>
              <div className="p-6">
                <div className="grid md:grid-cols-2 gap-6 mb-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-gray-800 mb-2">Current Loan</h3>
                    <div className="flex justify-between py-1"><span className="text-gray-600">Monthly</span><span className="font-medium">{fmt(res.currentMonthly)}</span></div>
                    <div className="flex justify-between py-1"><span className="text-gray-600">Remaining total</span><span className="font-medium">{fmt(res.currentTotal)}</span></div>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-blue-800 mb-2">New Loan</h3>
                    <div className="flex justify-between py-1"><span className="text-blue-700">Monthly</span><span className="font-medium">{fmt(res.newMonthly)}</span></div>
                    <div className="flex justify-between py-1"><span className="text-blue-700">New total</span><span className="font-medium">{fmt(res.newTotal)}</span></div>
                  </div>
                </div>
                <div className={`p-4 rounded-lg ${res.totalSaving > 0 ? 'bg-green-50' : 'bg-yellow-50'}`}>
                  <div className="grid md:grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="text-sm text-gray-600">Monthly saving</div>
                      <div className={`text-xl font-bold ${res.monthlySaving > 0 ? 'text-green-700' : 'text-yellow-700'}`}>{fmt(res.monthlySaving)}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">Total saving</div>
                      <div className={`text-xl font-bold ${res.totalSaving > 0 ? 'text-green-700' : 'text-yellow-700'}`}>{fmt(res.totalSaving)}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">Break-even</div>
                      <div className="text-xl font-bold text-gray-800">{res.breakEven ? `${res.breakEven} mo` : 'n/a'}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="mt-8 bg-gray-50 border border-gray-200 rounded-lg p-6 space-y-4">
            <h2 className="text-xl font-semibold text-gray-800">Frequently asked questions</h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-gray-700">When is refinancing worth it?</h3>
                <p className="text-gray-600">Generally when the new rate is meaningfully lower and you'll keep the loan past the break-even point. Watch out for extending the term, which can lower repayments but increase total interest.</p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-700">Where do I get my payout balance?</h3>
                <p className="text-gray-600">Ask your current lender for a payout figure. You can estimate it with our{' '}
                  <Link className="text-blue-600 hover:underline" to="/car-loan-payout-calculator/">car loan payout calculator</Link>.</p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-700">What costs are involved?</h3>
                <p className="text-gray-600">Typically an early exit/discharge fee on the old loan and an establishment fee on the new one. This tool finances those into the new loan and shows the break-even.</p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-700">Will refinancing affect my credit?</h3>
                <p className="text-gray-600">A new application involves a credit enquiry. Compare offers before applying. See our{' '}
                  <Link className="text-blue-600 hover:underline" to="/car-loan-calculator/">repayment calculator</Link> to model the new loan.</p>
              </div>
            </div>
            <p className="text-xs text-gray-500 pt-2">Estimates only and not financial advice. Actual savings depend on your lender's fees, exact payout figure and approval. Confirm with lenders before switching.</p>
          </div>
        </div>
      </Calculator>
    </>
  );
};

export default CarRefinanceCalculator;
